import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, map, switchMap, of, catchError, throwError } from 'rxjs';
import { SystemService } from '../services/system.service';

@Injectable({
  providedIn: 'root'
})
export class RecruitmentService {
  private companyRecruitmentCache = {};
  private recruitmentsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private matchingJobsSubject: BehaviorSubject<{ [recruitmentId: number]: any[] }> = new BehaviorSubject<{ [recruitmentId: number]: any[] }>({});

  constructor(private http: HttpClient, private system: SystemService) { }

  get recruitments$(): Observable<any[]> {
    return this.recruitmentsSubject.asObservable();
  }

  loadRecruitments(): void {
    this.http.get<any[]>(this.system.apiURL + '/recruitments').subscribe(recruitments => {
      this.recruitmentsSubject.next(recruitments);
    });
  }

  // Get recruitment by ID (first try local, then fallback to server if not found)
  getRecruitmentById(id: number): Observable<any> {
    return this.recruitments$.pipe(
      map(recruitments => recruitments.find(recruitment => recruitment.id === id)),
      switchMap(recruitment => {
        if (recruitment) {
          // If recruitment found locally, return it
          return of(recruitment);
        } else {
          // If not found locally, load from server
          return this.loadRecruitmentFromServer(id);
        }
      })
    );
  }

  // Get recruitment by ID (first try local, then fallback to server if not found)
  getJobById(id: number): Observable<any> {
    return this.recruitments$.pipe(
      map(recruitments => recruitments.find(recruitment => recruitment.id === id)),
      switchMap(recruitment => {
        if (recruitment) {
          // If recruitment found locally, return it
          return of(recruitment);
        } else {
          // If not found locally, load from server
          return this.loadJobFromServer(id);
        }
      })
    );
  }

  // Load recruitment from server and add to local cache
  private loadJobFromServer(id: number): Observable<any> {
    return this.http.get<any>(`${this.system.apiURL}/public/jobs/${id}`).pipe(
      tap(recruitment => {
        // Add the newly fetched recruitment to the local store
        const updatedRecruitments = [...this.recruitmentsSubject.value, recruitment];
        this.recruitmentsSubject.next(updatedRecruitments);
      })
    );
  }

  // Load recruitment from server and add to local cache
  private loadRecruitmentFromServer(id: number): Observable<any> {
    return this.http.get<any>(`${this.system.apiURL}/recruitments/${id}`).pipe(
      tap(recruitment => {
        // Add the newly fetched recruitment to the local store
        const updatedRecruitments = [...this.recruitmentsSubject.value, recruitment];
        this.recruitmentsSubject.next(updatedRecruitments);
      })
    );
  }

  createRecruitment(recruitment: any): Observable<any> {
    return this.http.post<any>(this.system.apiURL + '/recruitments', recruitment).pipe(
      tap(newRecruitment => {
        this.recruitmentsSubject.next([...this.recruitmentsSubject.value, newRecruitment]);
      })
    );
  }

  updateRecruitment(id: number, recruitment: any): Observable<any> {
    return this.http.put<any>(`${this.system.apiURL + '/recruitments'}/${id}`, recruitment).pipe(
      tap(updatedRecruitment => {
        const updatedRecruitments = this.recruitmentsSubject.value.map(r => (r.id === id ? updatedRecruitment : r));
        this.recruitmentsSubject.next(updatedRecruitments);
      })
    );
  }

  deleteRecruitment(id: number): Observable<any> {
    return this.http.delete<any>(`${this.system.apiURL + '/recruitments'}/${id}`).pipe(
      tap(() => {
        // After successful deletion, update the recruitments list
        const updatedRecruitments = this.recruitmentsSubject.value.filter(r => r.id !== id);
        this.recruitmentsSubject.next(updatedRecruitments);
      })
    );
  }

  addCandidateToRecruitment(recruitmentId: number, data){
    return this.http.post<any>(this.system.apiURL + '/recruitments/add-candidate/' + recruitmentId, data);
  }

  // Get matching jobs for an recruitment and save them in the subject
  getSimilarJobs(recruitmentId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.system.apiURL}/recruitments/${recruitmentId}/similar-jobs`).pipe(
      tap(matchingJobs => {
        // Get the current value of matching jobs
        const currentMatchingJobs = this.matchingJobsSubject.value;
        
        // Add or update the matching jobs for the current recruitment
        const updatedMatchingJobs = {
          ...currentMatchingJobs,
          [recruitmentId]: matchingJobs
        };
        
        // Update the subject with new matching jobs
        this.matchingJobsSubject.next(updatedMatchingJobs);
      })
    );
  }

  generateJobDescription(jobTitle: string): Observable<any> {
    const body = {
      jobTitle: jobTitle
    };

    return this.http.post<any>(`${this.system.apiURL}/ai/generate-jd`, body);
  }

  getRecruitmentsByCompany(forceRefresh: boolean = false): Observable<any> {
    // Check if the cache exists and is still valid or if force refresh is requested
    if (this.companyRecruitmentCache && !forceRefresh) {
      return of(this.companyRecruitmentCache); // Return cached data if it exists and no force refresh is required
    }

    // Make an HTTP request to fetch the recruitments from the server
    return this.http.get<any>(`${this.system.apiURL}/recruitments/company`).pipe(
      tap((data) => {
        this.companyRecruitmentCache = data; // Store the result in cache
      }),
      catchError((error) => {
        console.error('Error fetching recruitments by company', error);
        return throwError(error); // Handle errors appropriately
      })
    );
  }


  getRecruitmentWorkflowPersonsByMeeting(id: number) {
    return this.http.get<any[]>(`${this.system.apiURL}/recruitments/${id}/availabilities`);
  }
}
